import { useState, useRef, useEffect, useCallback, Fragment } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Box, Stack, Checkbox, Skeleton } from '@mui/material';
import { Pagination } from './pagination.component';
import style from './styles/table-styles.component.module.css';
import { FoliosType } from '../pages/devoluciones-sua/interfaces/seguimiento';

interface IDataTableProps {
	headers: { id: string; name: string }[];
	data: any[];
	align?: 'left' | 'right' | 'center' | 'justify' | 'inherit';
	pagination?: boolean;
	rowsPerPage?: number;
	labelStyles?: React.CSSProperties;
	messageEmpty?: string;
	onChangeCheckHeader?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onChangeCheckRow?: (
		e: React.ChangeEvent<HTMLInputElement>,
		selectRow: FoliosType
	) => void;
	headerCheck?: boolean;
	disabledRows?: string[];
	onSelectedRowsChange?: (selectedRows: any[]) => void;
	selectable?: boolean;
	isLoading?: boolean;
	idKey?: string;
}

export default function DataTable({
	headers,
	data,
	rowsPerPage = 10,
	pagination = true,
	labelStyles,
	messageEmpty,
	align,
	disabledRows = [],
	onSelectedRowsChange,
	onChangeCheckHeader,
	onChangeCheckRow,
	headerCheck,
	selectable = false,
	isLoading = false,
	idKey = 'id',
}: IDataTableProps) {
	const [page, setPage] = useState<number>(1);
	const [scroll, setScroll] = useState<boolean>(false);
	const [selectedRows, setSelectedRows] = useState<string[]>([]);
	const count = Math.ceil(data.length / rowsPerPage);
	const startDataPerPage = (page - 1) * rowsPerPage;
	const endDataPerPage = startDataPerPage + rowsPerPage;
	const [dataCopy, setDataCopy] = useState(data);

	const ParNumber = (number: number) => number % 2 === 0;

	const useHorizontalScrollEvent = (callback: any) => {
		const positionRef = useRef(0);
		return (e: any) => {
			const x = e.currentTarget.scrollLeft;
			if (x !== positionRef.current) {
				positionRef.current = x;
				callback(positionRef.current);
			}
		};
	};

	const handleScroll = useHorizontalScrollEvent((position: number) => {
		setScroll(position !== 0);
	});

	useEffect(() => {
		setPage(1);
		setDataCopy(data);
	}, [data]);

	// Selección individual de filas
	const handleClick = useCallback(
		(id: string) => {
			setSelectedRows((prevSelectedRows) => {
				const selectedIndex = prevSelectedRows.indexOf(id);
				let newSelected: string[] = [];

				if (selectedIndex === -1) {
					newSelected = newSelected.concat(prevSelectedRows, id);
				} else if (selectedIndex === 0) {
					newSelected = newSelected.concat(prevSelectedRows.slice(1));
				} else if (selectedIndex === prevSelectedRows.length - 1) {
					newSelected = newSelected.concat(prevSelectedRows.slice(0, -1));
				} else if (selectedIndex > 0) {
					newSelected = newSelected.concat(
						prevSelectedRows.slice(0, selectedIndex),
						prevSelectedRows.slice(selectedIndex + 1)
					);
				}

				return newSelected;
			});
		},
		[idKey]
	);

	const handleOnChangeCheckRow = (
		e: React.ChangeEvent<HTMLInputElement>,
		selectRow: FoliosType
	) => {
		const checked = e.target.checked;
		const updatedData = dataCopy.map((row) =>
			row.E_FOLIO === selectRow.E_FOLIO
				? {
						...row,
						checked:
							row.E_PERIODO === selectRow.E_PERIODO ? checked : row.checked,
				  }
				: row
		);
		setDataCopy(updatedData);
		onChangeCheckRow!(e, selectRow);
	};

	// Notificar cambios al padre, excluyendo disabledRows para que no aparezcan en la data final
	useEffect(() => {
		if (onSelectedRowsChange) {
			const selectedData = data.filter(
				(row) =>
					selectedRows.includes(row[idKey]) &&
					!disabledRows.includes(row[idKey])
			);
			onSelectedRowsChange(selectedData);
		}
	}, [selectedRows, data, idKey, onSelectedRowsChange]);

	// Cuando cambia la data, reseteamos la página y la selección
	useEffect(() => {
		setPage(1);
		setSelectedRows([]);
	}, [data]);

	return (
		<Fragment>
			<TableContainer
				id={style.tabla}
				component={Paper}
				sx={{ ...labelStyles }}
				onScroll={handleScroll}
			>
				<Table>
					<TableHead className={style.header} style={{ textAlign: align }}>
						<TableRow>
							{/* Si "selectable" es true, mostramos solo el "espacio en blanco" sin checkbox global */}
							{selectable && (
								<th className={style.headerCheckbox}>
									{/* Se elimina el Checkbox de seleccionar todo */}
								</th>
							)}
							{headers.map((header, index) => (
								<th
									className={`${
										index === 0 ? style.stickyHeader : ''
									} ${style.headerTitle} ${
										index === 0 && scroll ? style.scroll : ''
									}`}
									key={header.id}
								>
									{header.id === 'checked' ? (
										<Checkbox
											name="header-checked"
											checked={headerCheck}
											onChange={(e) => onChangeCheckHeader!(e)}
											sx={{
												color: '#ffffff',
												'&path': {
													outline: '1px solid #ffffff',
												},
												'&.Mui-checked': {
													color: '#3ABE21',
												},
											}}
										/>
									) : (
										header.name
									)}
								</th>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{isLoading ? (
							Array.from(new Array(rowsPerPage)).map((_, index) => (
								<TableRow key={index}>
									{selectable && (
										<td
											className={
												ParNumber(index) ? style.par : style.impar
											}
										>
											<Skeleton
												variant="rectangular"
												width={24}
												height={24}
											/>
										</td>
									)}
									{headers.map((header, index2) => (
										<td
											key={index2}
											style={{ ...labelStyles, textAlign: align }}
											className={`
                        ${ParNumber(index) ? style.par : style.impar} 
                        ${index2 === 0 ? style.sticky : ''}
                        ${index2 === 0 && scroll ? style.scroll : ''}
                      `}
										>
											<Skeleton variant="text" />
										</td>
									))}
								</TableRow>
							))
						) : data.length === 0 ? (
							<TableRow>
								<td
									colSpan={headers.length + (selectable ? 1 : 0)}
									className={style.empty}
								>
									{messageEmpty ? messageEmpty : 'Sin resultados'}
								</td>
							</TableRow>
						) : (
							dataCopy
								.slice(startDataPerPage, endDataPerPage)
								.map((row, index) => (
									<TableRow key={index}>
										{selectable && (
											<td
												className={`${
													ParNumber(index)
														? style.par
														: style.impar
												}`}
											>
												{disabledRows.includes(row[idKey]) ? (
													<Checkbox
														checked
														disabled
														sx={{
															color: '#293990',
															'&.Mui-checked': {
																color: '#969696',
															},
														}}
													/>
												) : (
													<Checkbox
														checked={selectedRows.includes(
															row[idKey]
														)}
														onChange={() =>
															handleClick(row[idKey])
														}
														sx={{
															color: '#293990', // Color del ícono cuando no está marcado
															'&.Mui-checked': {
																color: '#3ABE21', // Color del ícono cuando está marcado
															},
														}}
													/>
												)}
											</td>
										)}

										{headers.map((header, index2) => (
											<td
												style={{
													...labelStyles,
													textAlign: align,
												}}
												key={header.id}
												className={`
                        ${ParNumber(index) ? style.par : style.impar} 
                        ${index2 === 0 ? style.sticky : ''} 
                        ${index2 === 0 && scroll ? style.scroll : ''}
                      `}
											>
												{(row.status === '0' ||
													row.status === '01') &&
												header.id === 'selecCheck' ? (
													<Checkbox
														id="acceptTerms"
														name={row['nss']}
														checked={row['checked']}
														onChange={(e) =>
															handleOnChangeCheckRow(e, row)
														}
														inputProps={{
															'aria-label': 'controlled',
														}}
														sx={{
															color: '#293990',
															'&.Mui-checked': {
																color: '#3ABE21',
															},
														}}
													/>
												) : (
													row[header.id]
												)}
											</td>
										))}
									</TableRow>
								))
						)}
					</TableBody>
				</Table>
			</TableContainer>
			{pagination && count > 1 && (
				<Box display="flex" justifyContent="center" flex={1} marginTop="20px">
					<Stack spacing={2} className={style.stack}>
						<Pagination
							showFirstButton
							showLastButton
							count={count}
							onChange={(_e, page) => setPage(page)}
						/>
					</Stack>
				</Box>
			)}
		</Fragment>
	);
}
